import React from 'react'
import PropTypes from 'prop-types'
import { graphql, Link } from 'gatsby'
import dompurify from 'dompurify'
import { Container, Grid, Column, Spacer, Portrait } from 'ascential-horizon'
import getThemeConfig from '../providers/config/themeConfig'
import { trackLinkClick } from '../services/tracking'
import { windowExists } from '../utils'
import { useHasMounted } from '../utils/hooks'
import Layout from '../Layout'
import Breadcrumbs from '../components/Breadcrumbs'
import ShareButtons from '../components/ShareButtons'

import {
  TalentLink,
  TalentIcon,
  TalentSession,
} from '../components/Talent/Talents.style'

const TemplateTalentPage = ({ data, pageContext, location }) => {
  const hasMounted = useHasMounted()
  const {
    eventDate,
    speakers: { pathPlural, pathSingular, shareButtons },
    videoPlatform: { multiBrand, pagePrefix, timezoneIndicator },
    showBreadcrumbs,
    name,
  } = getThemeConfig()
  let sanitizer = () => {}

  if (hasMounted) {
    sanitizer = dompurify.sanitize
  }

  const {
    home: { header, footer },
  } = data
  const {
    speaker,
    pageType,
    breadcrumb: { crumbs },
  } = pageContext

  const layoutProps = {
    header,
    footer,
    pageType,
    pageContext,
    location,
  }

  // TODO not that ugly anymore, but probably still better to move somewhere else
  if (pagePrefix) {
    crumbs[2] = {
      pathname: `/${pagePrefix.slug}/${pathPlural}`,
      crumbLabel: pathPlural,
    }
  } else {
    crumbs[1] = { pathname: `/${pathPlural}`, crumbLabel: pathPlural }
  }

  const breadcrumbProps = {
    crumbs,
    crumbLabel: pageContext.pageTitle,
    location,
  }

  const timeIndicator = multiBrand
    ? timezoneIndicator[speaker.applicationId]
    : timezoneIndicator

  const currentUrl = windowExists && window.location.href
  const shareButtonsMsg = `Check out ${speaker.name}, ${speaker.jobTitle}, ${
    speaker.company
  }, at ${name}${eventDate ? `, ${eventDate}` : ''}`

  return (
    <Layout {...layoutProps}>
      {showBreadcrumbs && <Breadcrumbs {...breadcrumbProps} />}
      <Spacer space="xxlarge">
        <Container>
          <Grid>
            <Column width={[12, 12, 12, 2]} />
            <Column width={[12, 12, 12, 10]}>
              <TalentLink>
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault()
                    return windowExists && window.history.back()
                  }}>
                  <TalentIcon name="ChevronRight" size="20px" color="#707070" />
                  Go Back
                </a>
              </TalentLink>
            </Column>
          </Grid>
          <Grid>
            <Column width={[12, 12, 12, 2]} />
            <Column width={[12, 12, 6, 4]} mb={['large', 0, 0, 0]}>
              <div>
                <Portrait {...speaker} image={{ src: speaker.image }} />
              </div>
            </Column>
            <Column width={[12, 12, 6, 4]}>
              <div
                dangerouslySetInnerHTML={{ __html: sanitizer(speaker.bio) }}
              />

              {speaker.events && speaker.events.length > 0 && (
                <>
                  <h3>Appears in:</h3>
                  {speaker.events.map((event) => {
                    let Tag = 'div'
                    let tagProps = {}
                    if (event.hasUrl && event.url) {
                      Tag = Link
                      tagProps = {
                        to: event.url,
                        onClick: () => trackLinkClick(event.title, event.url),
                      }
                    }

                    return (
                      <TalentSession key={event.id}>
                        <Tag {...tagProps}>
                          <p>
                            <strong>{event.title}</strong>
                          </p>
                          <p>
                            {event.date}
                            {event.startTime ? ` - ${event.startTime}` : ''}
                            {event.endTime ? ` - ${event.endTime}` : ''}
                            {event.startTime || event.endTime
                              ? ` (${timeIndicator})`
                              : ''}
                          </p>
                        </Tag>
                      </TalentSession>
                    )
                  })}
                </>
              )}
              {shareButtons && (
                <ShareButtons
                  description={shareButtonsMsg}
                  title={shareButtonsMsg}
                  subject={`Check out this ${pathSingular} at ${name}!`}
                  message={`${shareButtonsMsg}, ${currentUrl}`}
                />
              )}
            </Column>
          </Grid>
        </Container>
      </Spacer>
    </Layout>
  )
}

TemplateTalentPage.propTypes = {
  pageContext: PropTypes.oneOfType([PropTypes.object]).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  data: PropTypes.oneOfType([PropTypes.object]).isRequired,
}

export const templateTalentPageQuery = graphql`
  query($rootId: String!) {
    home: contentfulTemplateHomePage(id: { eq: $rootId }) {
      ...header
      footer {
        ...footerQuery
      }
    }
  }
`

export default TemplateTalentPage
